/**
 * Browser-side structured JSON logging utility
 *
 * This module provides a structured logging system for client components with:
 *
 * - JSON-formatted log entries for consistent format across server and client
 * - Log levels: debug, info, warn, error
 * - Context support for component/function identification
 * - Environment-aware log levels (reduced output in production)
 * - Pretty printing in development for browser dev tools readability
 *
 * @example
 * 	;```typescript
 * 	import { clientLogger } from '@/src/core/utils/clientLogger'
 *
 * 	// Basic logging
 * 	clientLogger.info('Payment initiated', { orderId: '123', amount: 100 })
 *
 * 	// With context
 * 	const log = clientLogger.withContext('PaymentForm')
 * 	log.info('Form submitted')
 *
 * 	// Error logging
 * 	clientLogger.error('Payment failed', new Error('Network error'), { orderId: '123' })
 * 	```
 */

/** Log levels in order of severity */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/** Numeric values for log level comparison */
const LOG_LEVEL_VALUES: Record<LogLevel, number> = {
	debug: 0,
	info: 1,
	warn: 2,
	error: 3,
}

/** Structured log entry format for client-side logging */
export interface ClientLogEntry {
	/** ISO 8601 timestamp */
	timestamp: string
	/** Log level */
	level: LogLevel
	/** Log message */
	message: string
	/** Context identifier (e.g., component name, function name) */
	context?: string
	/** Additional metadata */
	metadata?: Record<string, unknown>
	/** Error details when logging errors */
	error?: SerializedClientError
}

/** Serialized error format for client-side structured logging */
export interface SerializedClientError {
	/** Error name/type */
	name: string
	/** Error message */
	message: string
	/** Stack trace (only included in development) */
	stack?: string
}

/** Client logger configuration options */
export interface ClientLoggerConfig {
	/** Minimum log level to output */
	minLevel: LogLevel
	/** Default context for all log entries */
	defaultContext?: string
	/** Whether to pretty print JSON (for development) */
	prettyPrint?: boolean
}

/** Client logger interface defining the public API */
export interface IClientLogger {
	/**
	 * Log a debug message
	 *
	 * @param message - The log message
	 * @param metadata - Optional additional metadata
	 */
	debug(message: string, metadata?: Record<string, unknown>): void

	/**
	 * Log an info message
	 *
	 * @param message - The log message
	 * @param metadata - Optional additional metadata
	 */
	info(message: string, metadata?: Record<string, unknown>): void

	/**
	 * Log a warning message
	 *
	 * @param message - The log message
	 * @param metadata - Optional additional metadata
	 */
	warn(message: string, metadata?: Record<string, unknown>): void

	/**
	 * Log an error message with optional error object
	 *
	 * @param message - The log message
	 * @param error - Optional error object to serialize
	 * @param metadata - Optional additional metadata
	 */
	error(message: string, error?: unknown, metadata?: Record<string, unknown>): void

	/**
	 * Create a child logger with a specific context
	 *
	 * @param context - The context identifier
	 * @returns A new logger instance with the context
	 */
	withContext(context: string): IClientLogger
}

/** Check if running in production environment */
function isProduction(): boolean {
	return process.env.NODE_ENV === 'production'
}

/** Check if running in test environment */
function isTest(): boolean {
	return process.env.NODE_ENV === 'test'
}

/**
 * Serialize an error object for structured logging
 *
 * @param error - The error to serialize
 * @param includeStack - Whether to include stack trace (only in development)
 * @returns Serialized error object
 */
export function serializeClientError(error: unknown, includeStack = true): SerializedClientError {
	if (error instanceof Error) {
		const serialized: SerializedClientError = {
			name: error.name,
			message: error.message,
		}

		// Only include stack in development for easier debugging
		if (includeStack && !isProduction()) {
			serialized.stack = error.stack
		}

		return serialized
	}

	// Handle non-Error objects
	return {
		name: 'UnknownError',
		message: String(error),
	}
}

/**
 * Client-side structured JSON logger implementation
 *
 * Provides browser-compatible JSON logging with support for:
 *
 * - Multiple log levels
 * - Context identification
 * - Error serialization
 * - Child loggers with inherited context
 */
class ClientLogger implements IClientLogger {
	private config: ClientLoggerConfig
	private context?: string

	/**
	 * Create a new ClientLogger instance
	 *
	 * @param config - Logger configuration options
	 * @param context - Optional context identifier
	 */
	constructor(config: ClientLoggerConfig, context?: string) {
		this.config = config
		this.context = context ?? config.defaultContext
	}

	/** Check if a log level should be output based on minimum level */
	private shouldLog(level: LogLevel): boolean {
		return LOG_LEVEL_VALUES[level] >= LOG_LEVEL_VALUES[this.config.minLevel]
	}

	/** Format and output a log entry */
	private log(level: LogLevel, message: string, error?: unknown, metadata?: Record<string, unknown>): void {
		if (!this.shouldLog(level)) {
			return
		}

		const entry: ClientLogEntry = {
			timestamp: new Date().toISOString(),
			level,
			message,
		}

		if (this.context) {
			entry.context = this.context
		}

		if (metadata && Object.keys(metadata).length > 0) {
			entry.metadata = metadata
		}

		if (error !== undefined) {
			entry.error = serializeClientError(error, !isProduction())
		}

		const output = this.config.prettyPrint ? JSON.stringify(entry, undefined, 2) : JSON.stringify(entry)

		// Use appropriate console method for each level
		switch (level) {
			case 'debug': {
				console.debug(output)
				break
			}
			case 'info': {
				console.info(output)
				break
			}
			case 'warn': {
				console.warn(output)
				break
			}
			case 'error': {
				console.error(output)
				break
			}
		}
	}

	debug(message: string, metadata?: Record<string, unknown>): void {
		this.log('debug', message, undefined, metadata)
	}

	info(message: string, metadata?: Record<string, unknown>): void {
		this.log('info', message, undefined, metadata)
	}

	warn(message: string, metadata?: Record<string, unknown>): void {
		this.log('warn', message, undefined, metadata)
	}

	error(message: string, error?: unknown, metadata?: Record<string, unknown>): void {
		this.log('error', message, error, metadata)
	}

	withContext(context: string): IClientLogger {
		return new ClientLogger(this.config, context)
	}
}

/** Determine the minimum log level based on environment */
function getMinLogLevel(): LogLevel {
	// In production, only log warnings and errors to reduce noise
	if (isProduction()) {
		return 'warn'
	}

	// In test environment, only log errors to reduce noise
	if (isTest()) {
		return 'error'
	}

	// In development, log everything
	return 'debug'
}

/** Determine if pretty printing should be enabled */
function shouldPrettyPrint(): boolean {
	// Pretty print in development for browser dev tools readability
	return !isProduction() && !isTest()
}

/** Default client logger configuration */
const defaultConfig: ClientLoggerConfig = {
	minLevel: getMinLogLevel(),
	prettyPrint: shouldPrettyPrint(),
}

/**
 * Singleton client logger instance for application-wide use
 *
 * @example
 * 	;```typescript
 * 	import { clientLogger } from '@/src/core/utils/clientLogger'
 *
 * 	clientLogger.info('Component mounted')
 * 	clientLogger.error('API call failed', error, { endpoint: '/api/payment' })
 *
 * 	// With context
 * 	const log = clientLogger.withContext('PaymentForm')
 * 	log.info('Form submitted', { amount: 100 })
 * 	```
 */
export const clientLogger: IClientLogger = new ClientLogger(defaultConfig)

/**
 * Create a new client logger instance with custom configuration
 *
 * @example
 * 	;```typescript
 * 	const verboseLogger = createClientLogger({ minLevel: 'debug', prettyPrint: true })
 * 	verboseLogger.debug('Detailed information')
 * 	```
 *
 * @param config - Partial configuration to override defaults
 * @returns A new ClientLogger instance
 */
export function createClientLogger(config: Partial<ClientLoggerConfig> = {}): IClientLogger {
	return new ClientLogger({
		...defaultConfig,
		...config,
	})
}
